import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import OrganizationRepository from "../../repositories/OrganizationRepository";
import { useLazyGetOrganizationAccessQuery } from "../../slices/employeesApiSlice";
import {
    addOrganizationAccessInfo,
    addSelectedOrganization,
} from "../../slices/organizationSlice";
import { clearHistory } from "../../slices/historySlice";
import { useLazyGetPortalUserInfoQuery } from "../../slices/dataviewApiSlice";

const useSelectedOrganizationsObserver = () => {
    const dispatch = useDispatch();

    const {
        selectedOrganizations: userSelectedOrganizations,
        organizationsAccess,
    } = useSelector((state) => state.organizations);

    //selected organization in report page
    const { selectedOrganizationsReports: userSelectedOrganizationReports } =
        useSelector((state) => state.reportOrganization);

    const { loaded, organizations: allOrganizations } = useSelector(
        (state) => state.userDetails
    );
    const { flags } = useSelector((state) => state.app);
    const { user } = useSelector((state) => state.auth);

    const url = window.location.pathname;

    const [getOrganizationsAccess, { data: organizationsAccessData = null }] =
        useLazyGetOrganizationAccessQuery();

    const [
        getPortalUser,
        { data: portalUserData, isError, isFetching: isFetchingUserData },
    ] = useLazyGetPortalUserInfoQuery();

    useEffect(() => {
        if (!loaded) return;

        const orgIds = [];

        for (const [organizationId, organization] of Object.entries(
            userSelectedOrganizations
        )) {
            if (organization.organization === null) {
                orgIds.push(parseInt(organizationId));
            }
        }

        updateOrganizations(orgIds);

        const selectedIds = OrganizationRepository.getSelectedOrganizationIds(
            url.includes("reportingcapabilities")
                ? userSelectedOrganizationReports
                : userSelectedOrganizations
        );
        const accessIds = [];

        selectedIds.forEach((selectedId) => {
            if (!(selectedId in organizationsAccess)) {
                accessIds.push(selectedId);
            }
        });

        if (accessIds.length > 0) {
            let accessIdsString = accessIds.join(",");
            if (flags?.isODSEnabled) {
                getPortalUser({
                    isfUserId: String(user.userId).toUpperCase(),
                    organizationId: accessIdsString,
                });
            } else {
                getOrganizationsAccess({
                    organizationIds: accessIdsString,
                });
            }
        }
    }, [userSelectedOrganizations, loaded, userSelectedOrganizationReports]);

    const updateOrganizations = (ids) => {
        ids.forEach((id) => {
            const organization = OrganizationRepository.getOrganizationById(
                allOrganizations,
                id
            );

            if (organization === null) {
                dispatch(clearHistory());
            } else {
                const related = OrganizationRepository.getRelatedOrganizations(
                    allOrganizations,
                    organization
                );

                dispatch(
                    addSelectedOrganization({
                        id: organization.ORGANIZATIONID,
                        organization: organization,
                        related,
                    })
                );
            }
        });
    };

    const mapSsnAccessFromVisibilityDesc = (visibilityDesc) => {
        const normalizedVisibility = (visibilityDesc || "")
            .trim()
            .toLowerCase();

        if (normalizedVisibility === "mask display") return "Partial";
        if (normalizedVisibility === "display") return "Full";
        if (
            normalizedVisibility === "do not display" ||
            normalizedVisibility === "no display" ||
            normalizedVisibility === "no access"
        )
            return "No Access";

        return "No Access";
    };

    const transformOdsAccessData = ({ assignedOrgs } = {}) =>
        assignedOrgs?.map(({ orgId, accessDetails = [] }) => {
            const accessMap = accessDetails.reduce((acc, detail) => {
                acc[detail.accessElementAbrv] =
                    detail.accessElementVisibilityAbrv;
                return acc;
            }, {});

            const OrgSsnAccess = accessDetails.find(
                (detail) => detail?.accessElementAbrv === "Employee SSN"
            );

            return {
                OrganizationId: orgId?.toLowerCase(),
                SSN: mapSsnAccessFromVisibilityDesc(
                    OrgSsnAccess?.accessElementVisibilityDesc
                ),
                HistoricalLeave: accessMap["Claim/Leave History"],
                SelfInsuredClaim: accessMap["Self Insured Claim"],
            };
        }) || [];

    useEffect(() => {
        if (
            organizationsAccessData !== null &&
            Array.isArray(organizationsAccessData.orgAccessList)
        ) {
            dispatch(
                addOrganizationAccessInfo(organizationsAccessData.orgAccessList)
            );
        }
        if (portalUserData != null && portalUserData.userPermissions) {
            const transformedAccessData = transformOdsAccessData(
                portalUserData.userPermissions
            );
            dispatch(addOrganizationAccessInfo(transformedAccessData));
        }
    }, [organizationsAccessData, portalUserData, dispatch]);

    return;
};

export default useSelectedOrganizationsObserver;
